/*
 * Teilschema: Zeichnung links, Wertfelder rechts, alles in der Eigengroesse
 * der Vorlage gesetzt und als Ganzes auf den Kasten skaliert.
 *
 * Skaliert und nicht umbrochen: die Fuehler in der Zeichnung zeigen mit
 * gestrichelten Linien genau auf ihr Feld. Ein Umbruch der Wertspalte risse
 * diese Zuordnung auseinander.
 *
 * Die Zeichnung kommt als fertiges SVG aus dem Generator
 * (dev/scripts/hmi-teilschemen/s400.py) und wird eingesetzt, nicht
 * nachgebaut. Live sind daran zwei Dinge: Pumpen laufen oder stehen
 * (`data-run` an ihrer Gruppe) und die Fliessmarken laufen, solange eine
 * Pumpe des Teilschemas laeuft. Die Werte stehen in Preact-Feldern daneben.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { SchematicPartWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { coerceBool, formatLeafValue } from '../../format/leafValue';
import { loadS400Svgs, s400Part, type S400Field, type S400Part } from './s400/index';
import './schematicPart.css';

/** Polster und Kopf der Karte, wie in der Vorlage. */
const PAD_X = 12;
const PAD_Y = 10;
const HEAD_H = 24;
const GAP = 6;

export function SchematicPartRenderer({ widget, source }: { widget: SchematicPartWidget; source: IPointBindingSource }) {
  const part = s400Part(widget.part);
  if (!part) {
    return (
      <div class="hv-sp hv-sp--missing" role="alert">
        Teilschema „{widget.part}“ ist nicht im Katalog.
      </div>
    );
  }
  return <PartBody widget={widget} part={part} source={source} />;
}

function PartBody({ widget, part, source }: { widget: SchematicPartWidget; part: S400Part; source: IPointBindingSource }) {
  const svg = useSvg(part.code);
  const outer = useRef<HTMLDivElement>(null);
  const draw = useRef<HTMLDivElement>(null);
  const showHeader = widget.showHeader !== false;
  const cardH = part.h - (showHeader ? 0 : HEAD_H + GAP);
  const { scale, dx, dy } = useFitScale(outer, part.w, cardH);

  // Pumpenzustand. Nur die Schalterfelder, die einer Pumpe gehoeren.
  const pumpRuns = usePumpStates(part, widget.bindings, source);
  const anyRunning = Object.values(pumpRuns).some((v) => v === true);

  useLayoutEffect(() => {
    const host = draw.current;
    if (!host || !svg) return;
    for (const [sid, running] of Object.entries(pumpRuns)) {
      const g = host.querySelector(`g[data-sid="${cssEscape(sid)}"]`);
      if (g) g.setAttribute('data-run', running === null ? 'unknown' : running ? '1' : '0');
    }
  });

  const columnX = PAD_X + part.svgW + GAP;
  const columnW = part.w - columnX - PAD_X;
  const bodyTop = PAD_Y + (showHeader ? HEAD_H + GAP : 0);

  return (
    <div class="hv-sp" ref={outer}>
      <div
        class={`hv-sp__card${anyRunning ? ' hv-sp__card--flow' : ''}`}
        style={{ width: `${part.w}px`, height: `${cardH}px`, left: `${dx}px`, top: `${dy}px`, transform: `scale(${scale})` }}
      >
        {showHeader && (
          <div class="hv-sp__head" style={{ left: `${PAD_X}px`, right: `${PAD_X}px`, top: `${PAD_Y}px`, height: `${HEAD_H}px` }}>
            <span class="hv-sp__title">{widget.label || part.title}</span>
            <span class="hv-sp__code">{part.code}</span>
          </div>
        )}
        <div
          class="hv-sp__draw"
          ref={draw}
          style={{ left: `${PAD_X}px`, top: `${bodyTop}px`, width: `${part.svgW}px`, height: `${part.svgH}px` }}
          // Aus dem eigenen Generator, nicht aus Nutzereingaben.
          dangerouslySetInnerHTML={{ __html: svg ?? '' }}
        />
        <div class="hv-sp__fields" style={{ left: `${columnX}px`, top: `${bodyTop}px`, width: `${columnW}px`, height: `${part.svgH}px` }}>
          {part.fields.map((f) => (
            <PartField key={f.id} field={f} binding={widget.bindings[f.id]} source={source} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PartField({ field, binding, source }: { field: S400Field; binding: string | undefined; source: IPointBindingSource }) {
  const sig = binding ? pointSignal(source, binding) : null;
  const release = sig?.release;
  useEffect(() => release, [release]);
  const meta = useLeafMeta(binding);
  const current = sig?.value.value ?? null;
  const raw = current?.value;
  const degraded = current ? isDegraded(current) : false;
  const tone = field.tone ? `var(--${field.tone})` : undefined;
  const state = field.type === 'schalter' ? switchState(raw) : null;

  return (
    <div
      class={`hv-sp__field${binding ? '' : ' hv-sp__field--unbound'}${degraded ? ' hv-sp__field--stale' : ''}`}
      style={{ top: `${field.top}px`, height: `${field.height}px` }}
      title={binding ? `${field.name || field.label}` : `${field.name || field.label}: noch nicht gebunden`}
    >
      <div class="hv-sp__fieldText">
        <span class="hv-sp__label">{field.label}</span>
        {field.type !== 'schalter' && (
          <span class="hv-sp__value">
            <span class="hv-sp__num" style={tone ? { color: tone } : undefined}>
              {raw === undefined || raw === null
                ? '--'
                : typeof raw === 'number' && raw <= -999
                  ? '---'
                  : formatLeafValue(raw, meta, meta?.decimals ?? field.decimals)}
            </span>
            {field.unit && typeof raw !== 'string' && <span class="hv-sp__unit">{field.unit}</span>}
          </span>
        )}
      </div>
      {state !== null && (
        <span class={`hv-sp__pill hv-sp__pill--${state === undefined ? 'unknown' : state ? 'on' : 'off'}`}>
          <span class="hv-sp__dot" />
          {state === undefined ? '--' : state ? 'Ein' : 'Aus'}
        </span>
      )}
    </div>
  );
}

/** Ein/Aus aus einem Rohwert: true, Zahl > 0, "Ein"/"on". `undefined` ohne Wert. */
function switchState(raw: unknown): boolean | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number') return raw > 0;
  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (['ein', 'an', 'on', 'true', 'laeuft', 'läuft'].includes(t)) return true;
    if (['aus', 'off', 'false', '0'].includes(t)) return false;
    const n = Number(t);
    return Number.isFinite(n) ? n > 0 : undefined;
  }
  return coerceBool(raw) ?? undefined;
}

/** Je Pumpe: laeuft (true), steht (false), unbekannt (null, nicht gebunden). */
function usePumpStates(part: S400Part, bindings: Record<string, string>, source: IPointBindingSource): Record<string, boolean | null> {
  const subs = Object.entries(part.pumps).map(([sid, fieldId]) => {
    const key = bindings[fieldId];
    return { sid, sub: key ? pointSignal(source, key) : null };
  });
  useEffect(() => () => subs.forEach((s) => s.sub?.release()), [subs]);
  const out: Record<string, boolean | null> = {};
  for (const s of subs) {
    if (!s.sub) { out[s.sid] = null; continue; }
    out[s.sid] = switchState(s.sub.value.value?.value) ?? null;
  }
  return out;
}

function useSvg(code: string): string | null {
  const [svgs, setSvgs] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    let alive = true;
    loadS400Svgs().then((all) => { if (alive) setSvgs(all); }).catch(() => { /* bleibt leer, Felder zeigen trotzdem Werte */ });
    return () => { alive = false; };
  }, []);
  return svgs?.[code] ?? null;
}

/** Massstab, mit dem die Karte in den Kasten passt, ohne zu verzerren. */
function useFitScale(ref: { current: HTMLElement | null }, w: number, h: number): { scale: number; dx: number; dy: number } {
  const [fit, setFit] = useState({ scale: 1, dx: 0, dy: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw <= 0 || ch <= 0) return;
      const scale = Math.max(0.1, Math.min(cw / w, ch / h));
      // Mittig im Kasten; ein Kasten in anderem Seitenverhaeltnis laesst
      // links und rechts (oder oben und unten) gleich viel frei.
      setFit({ scale, dx: Math.max(0, (cw - w * scale) / 2), dy: Math.max(0, (ch - h * scale) / 2) });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, w, h]);
  return fit;
}

function cssEscape(s: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, '\\$&');
}
